import { useCallback, useEffect, useRef, useState } from "react";
import type { TtsProjectQuery } from "./queries";

type BlockType = NonNullable<TtsProjectQuery["ttsProject"]>["blocks"]["edges"][number]["node"];

export function useBlockPreview(blocks: BlockType[]): {
    isPreviewPlaying: boolean;
    playWithSilence: (canPreview: boolean) => Promise<void>;
    stopPreview: () => void;
} {
    const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
    const currentAudioRef = useRef<HTMLAudioElement | null>(null);
    const stopPreviewRef = useRef<(() => void) | null>(null);

    const stopPreview = useCallback(() => {
        setIsPreviewPlaying(false);
        if (currentAudioRef.current !== null) {
            currentAudioRef.current.pause();
            currentAudioRef.current.currentTime = 0;
        }
        if (stopPreviewRef.current !== null) {
            stopPreviewRef.current();
        }
    }, []);

    const playWithSilence = useCallback(
        async (canPreview: boolean) => {
            if (!canPreview || isPreviewPlaying) {
                return;
            }
            let stopRequested = false;
            stopPreviewRef.current = () => {
                stopRequested = true;
            };
            setIsPreviewPlaying(true);
            try {
                for (let i = 0; i < blocks.length; i += 1) {
                    const block = blocks[i];
                    if (!block.file?.src) {
                        continue;
                    }
                    const audio = new Audio(block.file.src);
                    currentAudioRef.current = audio;
                    await new Promise<void>((resolve) => {
                        audio.onended = () => {
                            resolve();
                        };
                        audio.onerror = () => {
                            resolve();
                        };
                        const playPromise = audio.play();
                        if (playPromise !== undefined) {
                            playPromise.catch(() => {
                                resolve();
                            });
                        }
                    });
                    if (stopRequested) {
                        break;
                    }
                    const isLast = i === blocks.length - 1;
                    if (!isLast && block.silenceAfter > 0) {
                        await new Promise<void>((resolve) => {
                            const timeoutId = window.setTimeout(() => {
                                resolve();
                            }, block.silenceAfter * 1000);
                            stopPreviewRef.current = () => {
                                stopRequested = true;
                                window.clearTimeout(timeoutId);
                                resolve();
                            };
                        });
                        if (stopRequested) {
                            break;
                        }
                    }
                }
            } finally {
                stopPreview();
                currentAudioRef.current = null;
                stopPreviewRef.current = null;
            }
        },
        [blocks, isPreviewPlaying, stopPreview],
    );

    useEffect(() => {
        return () => {
            stopPreview();
        };
    }, [stopPreview]);

    return { isPreviewPlaying, playWithSilence, stopPreview };
}
